import React, { useContext, useEffect, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import { useFetch } from 'use-http';
import '../common/BreadCrumb.scss';
import { Url } from 'src/api';
import { EasternTownshipTypeArray } from 'src/models/EasternTownshipType';

interface IBreadCrumbProps extends RouteComponentProps {
    clientName: string;
    clientNumber?: number;
    projectName: string;
}

const BreadCrumb = ({ clientName, projectName, history }: IBreadCrumbProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { get } = useFetch();

    const [townshipInfo, setTownshipInfo] = useState<string>('');
    const [sectionNumber, setSectionNumber] = useState<number>(0);
    const [easternLabel, setEasternLabel] = useState<string>('');

    const {
        location: { pathname },
    } = history;

    useEffect(() => {
        if (appContext.sectionNumber) {
            getTonwnshipInfo();
        }
    }, [appContext.sectionNumber]);

    useEffect(() => {
        if (appContext.mapNumber) {
            getEasternTownshipInfo();
        }
    }, [appContext.mapNumber]);

    const getTonwnshipInfo = async () => {
        const projectInfo = await get(Url.project.getProject(appContext, appContext.projectNumber));
        const sectionInfo = await get(Url.project.getSection(appContext, appContext.sectionNumber));

        switch (projectInfo?.landSystemId) {
            case 1:
                if (!!sectionInfo) {
                    setTownshipInfo(sectionInfo?.townshipDescription);
                    setSectionNumber(sectionInfo?.sectionNumber);
                }
                break;
            case 3:
            case 4:
                const eTownshipInfo = await get(Url.project.getTownship(appContext, sectionInfo?.townshipId));
                let block = eTownshipInfo?.block;
                let tNumber = eTownshipInfo?.townshipNumber;

                if (!!sectionInfo && tNumber !== 0) {
                    let tlabel = `T${eTownshipInfo?.townshipNumber}${eTownshipInfo?.townshipCardinal}`;
                    let name = `B${block} - ${tlabel}`;
                    setTownshipInfo(name);
                    setSectionNumber(sectionInfo?.sectionNumber);
                } else if (!!sectionInfo && !block) {
                    console.log('this should only work for east texas');
                    setTownshipInfo('');
                    setSectionNumber(0);
                }

                break;
            default:
                setTownshipInfo('');
                setSectionNumber(0);
                break;
        }
    };

    const getEasternTownshipInfo = async () => {
        const mapInfo = await get(Url.project.getEasternMap(appContext, appContext.mapNumber));
        const eTownshipInfo = await get(`${Url.project.getEasternTownship(appContext, mapInfo?.easternTownshipId)}`);
        let name = mapInfo?.name as string;
        let type = EasternTownshipTypeArray().find((e) => e.id === eTownshipInfo?.easternTownshipTypeId);
        let label = `${name} - ${type?.name}`;

        setEasternLabel(label);
    };

    const pathnames = pathname.split('/').filter((x: string) => x);
    const selectedPathNames = pathnames.slice(4, pathnames.length);

    const clientPath = `/clients/${appContext.clientNumber}/projects`;
    const projectPath = `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}`;
    const leasePath =
        projectPath +
        `/sections/${appContext.sectionNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}` +
        `/ownership/${appContext.ownershipId}/leases/${appContext.leaseId}/leaseholds/${appContext.leaseholdId}`;
    const easternLeasePath =
        projectPath +
        `/maps/${appContext.mapNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}` +
        `/ownership/${appContext.ownershipId}/leases/${appContext.leaseId}/leaseholds/${appContext.leaseholdId}`;

    const rightOfWayPath = `ownership/${appContext.ownershipId}/row/${appContext.rowId}/rowhold/${appContext.rowholdId}`;
    const surfaceWaiverPath = `ownership/${appContext.ownershipId}/surface-waivers/${appContext.surfaceWaiverId}/surface-waiver-holds/${appContext.surfaceWaiverHoldId}`;
    const solarLeasePath = `ownership/${appContext.ownershipId}/solar-leases/${appContext.solarLeaseId}/solar-lease-holds/${appContext.solarLeaseHoldId}`;
    const batteryStoragePath = `ownership/${appContext.ownershipId}/battery-storage/${appContext.batteryStorageId}/battery-storage-holds/${appContext.batteryStorageHoldId}`;
    const brineLeasePath = `ownership/${appContext.ownershipId}/brine-leases/${appContext.brineLeaseId}/brine-lease-holds/${appContext.brineLeaseHoldId}`;

    // BreadCrumb paths
    let appPaths = [
        {
            name: `${sectionNumber !== 0 ? sectionNumber + ' -' : ''} ${townshipInfo} (${appContext.sectionNumber})`,
            backTo: `${projectPath}/sections/${appContext.sectionNumber}/tracts/${appContext.tractNumber}`,
        },
        {
            name: `Tract ${appContext.tractNumber} - H${appContext.horizonNumber}`,
            backTo: `${projectPath}/sections/${appContext.sectionNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}`,
        },
        {
            name: `Leasehold - ${appContext.leaseholdId}`,
            backTo: `${projectPath}/sections/${appContext.sectionNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}`,
        },
        { name: 'Contract', backTo: `${projectPath}/acquisitions` },
        { name: `Lease - ${appContext.leaseId}`, backTo: `${projectPath}/acquisitions` },
        { name: `${easternLabel} (${appContext.mapNumber})`, backTo: `${projectPath}/tracts` },
        {
            name: `Tract ${appContext.tractNumber} - H${appContext.horizonNumber}`,
            backTo: `${projectPath}/maps/${appContext.mapNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}`,
        },
        {
            name: `Leasehold - ${appContext.leaseholdId}`,
            backTo: `${projectPath}/maps/${appContext.mapNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}`,
        },
    ];

    let surfaceProjectPaths = [
        {
            name: `${sectionNumber !== 0 ? sectionNumber + ' -' : ''} ${townshipInfo} (${appContext.sectionNumber})`,
            backTo: `${projectPath}/sections/${appContext.sectionNumber}/tracts`,
        },
        {
            name: `Surface Tract ${appContext.surfaceTractNumber}`,
            backTo: `${projectPath}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}`,
        },
    ];

    let surfaceContractPaths = [{ name: 'Contract', backTo: `${projectPath}/acquisitions` }];

    switch (pathname) {
        case `${projectPath}/tracts`:
        case `${projectPath}/sections/${appContext.sectionNumber}/tracts/`:
            appPaths = appPaths.slice(0, 1);
            break;
        case `${projectPath}/sections/${appContext.sectionNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}`:
            appPaths = appPaths.slice(0, 2);
            break;
        case leasePath:
            appPaths = appPaths.slice(0, 3);
            break;
        case `${projectPath}/acquisitions/lease-generation/ownerId/${appContext.ownerId}/leases/${appContext.leaseId}`:
            appPaths = appPaths.slice(3, 5);
            break;
        case `${projectPath}/maps/${appContext.mapNumber}/tracts/${appContext.tractNumber}`:
            appPaths = appPaths.slice(5, 6);
            break;
        case `${projectPath}/maps/${appContext.mapNumber}/tracts/${appContext.tractNumber}/horizons/${appContext.horizonNumber}`:
            appPaths = appPaths.slice(5, 7);
            break;
        case easternLeasePath:
            appPaths = appPaths.slice(5, 8);
            break;
        case `${projectPath}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}`:
            appPaths = surfaceProjectPaths.slice(0, 2);
            break;
        case `${projectPath}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}/${rightOfWayPath}`:
            let rightOfWayTitle = [{ name: `Right Of Way - ${appContext.rowId}`, backTo: '' }];
            appPaths = surfaceProjectPaths.concat(rightOfWayTitle);
            break;
        case `${projectPath}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}/${surfaceWaiverPath}`:
            let surfaceWaiverTitle = { name: `Surface Waiver - ${appContext.surfaceWaiverId}`, backTo: '' };
            appPaths = surfaceProjectPaths.concat(surfaceWaiverTitle);
            break;
        case `${projectPath}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}/${solarLeasePath}`:
            let solarLeaseTitle = { name: `Solar Lease - ${appContext.solarLeaseId}`, backTo: '' };
            appPaths = surfaceProjectPaths.concat(solarLeaseTitle);
            break;
        case `${projectPath}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}/${batteryStoragePath}`:
            let batteryStorageTitle = { name: `BESS - ${appContext.batteryStorageId}`, backTo: '' };
            appPaths = surfaceProjectPaths.concat(batteryStorageTitle);
            break;
        case `${projectPath}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}/${brineLeasePath}`:
            let brineLeaseTitle = { name: `Brine Lease - ${appContext.brineLeaseId}`, backTo: '' };
            appPaths = surfaceProjectPaths.concat(brineLeaseTitle);
            break;    
        case `${projectPath}/acquisitions/lease-generation/ownerId/${appContext.ownerId}/solar-leases/${appContext.solarLeaseId}`:
            let solarContractTitle = { name: `Solar Lease - ${appContext.solarLeaseId}`, backTo: '' };
            appPaths = surfaceContractPaths.concat(solarContractTitle);
            break;
        case `${projectPath}/acquisitions/lease-generation/ownerId/${appContext.ownerId}/surface-waivers/${appContext.surfaceWaiverId}`:
            let surfaceContractTitle = { name: `Surface Waiver Lease - ${appContext.surfaceWaiverId}`, backTo: '' };
            appPaths = surfaceContractPaths.concat(surfaceContractTitle);
            break;
        case `${projectPath}/acquisitions/lease-generation/ownerId/${appContext.ownerId}/battery-storage/${appContext.batteryStorageId}`:
            let bessContractTitle = { name: `BESS Lease - ${appContext.batteryStorageId}`, backTo: '' };
            appPaths = surfaceContractPaths.concat(bessContractTitle);
            break;
        case `${projectPath}/acquisitions/lease-generation/ownerId/${appContext.ownerId}/brine-leases/${appContext.brineLeaseId}`:
            let brineContractTitle = { name: `Brine Lease - ${appContext.brineLeaseId}`, backTo: '' };
            appPaths = surfaceContractPaths.concat(brineContractTitle);
            break;    
        default:
            appPaths = [];
            break;
    }

    // Maybe we would use a variation of this, if the BreadCrumb becomes too busy!

    // const nameShortener = (name: string) => {
    //     if (!!name.includes(' ')) {
    //         let firstLetter: Array<string> = [];
    //         let words = name.split(' ').filter((x: string) => x);
    //         words.forEach((word: string) => {
    //             let initalLetters = word.slice(0, 1);
    //             firstLetter.push(initalLetters);
    //         });

    //         let newName = firstLetter.join('');
    //         return (
    //             <abbr style={{ borderBottom: '1px solid white', textDecoration: 'unset' }} title={`${name}`}>
    //                 {newName}
    //             </abbr>
    //         );
    //     }
    //     return name;
    // };

    // let clientAbbr = nameShortener(clientName);
    // let projectAbbr = nameShortener(projectName);
    let clientNumber = appContext.clientNumber;
    let projectNumber = appContext.projectNumber;

    const capitalizeFunc = (name: string) => {
        let nameLength = name.length;
        let firstCapLetter = name.slice(0, 1).toUpperCase();
        let restOfName = name.slice(1, nameLength);
        let newName = `${firstCapLetter}${restOfName}`;

        return newName;
    };

    return (
        <>
            {pathnames?.length > 0 ? (
                <button
                    className="breadcrumbs__item"
                    onClick={() => {
                        history.push(clientPath);
                    }}
                >
                    {clientName} ({clientNumber}) - {projectName} ({projectNumber})
                </button>
            ) : (
                ''
            )}
            {selectedPathNames?.length <= 1
                ? selectedPathNames?.map((name: string, index: number) => {
                      const capName = capitalizeFunc(name);
                      const routeTo = `/${pathnames.slice(0, index + 4).join('/')}`;
                      const isLast = index + 4 === pathnames?.length - 1;

                      if (capName === 'Acquisitions') {
                          return (
                              isLast && (
                                  <button key={index} className="isActive">
                                      Contracts
                                  </button>
                              )
                          );
                      }
                      return isLast ? (
                          <button key={index} className="isActive">
                              {capName}
                          </button>
                      ) : (
                          <button
                              key={index}
                              className="breadcrumbs__item clickable"
                              onClick={() => {
                                  history.push(routeTo);
                              }}
                          >
                              {capName}
                          </button>
                      );
                  })
                : appPaths?.map((el, index) => {
                      const isLast = index + 1 === appPaths?.length;
                      return isLast ? (
                          <button key={index} className="isActive">
                              {el.name}
                          </button>
                      ) : (
                          <button
                              key={index}
                              className="breadcrumbs__item"
                              onClick={() => {
                                  history.push(el.backTo);
                              }}
                          >
                              {el.name}
                          </button>
                      );
                  })}
        </>
    );
};

export default withRouter(BreadCrumb);



// WEBPACK FOOTER //
// ./src/components/common/BreadCrumb.tsx